import React, { useState } from "react";
import {
  useGetProductSheets,
  useDeleteProductSheet,
  useDownloadProductSheet,
} from "./hooks";
import { ProductSheet } from "./api";
import ProductSheetModal from "./ProductSheetModal";
import { useI18n } from "../../i18n";

interface ProductSheetsListProps {
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const ProductSheetsList: React.FC<ProductSheetsListProps> = ({
  searchQuery,
  onSearchChange,
}) => {
  const { t, code } = useI18n();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingProductSheet, setEditingProductSheet] =
    useState<ProductSheet | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const {
    data: productSheetsData = [],
    isLoading,
    error,
  } = useGetProductSheets(searchQuery);

  // Ensure productSheets is always an array
  const productSheets = Array.isArray(productSheetsData)
    ? productSheetsData
    : [];
  const deleteProductSheetMutation = useDeleteProductSheet();
  const downloadProductSheetMutation = useDownloadProductSheet();

  const handleDelete = (id: string, sheetName: string) => {
    if (window.confirm(t("productSheets.confirmDelete", { name: sheetName }))) {
      deleteProductSheetMutation.mutate(id);
    }
  };

  const handleDownload = (id: string) => {
    downloadProductSheetMutation.mutate(id);
  };

  const handleEdit = (productSheet: ProductSheet) => {
    setEditingProductSheet(productSheet);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingProductSheet(null);
  };

  const getCategoryBadgeColor = (category: string) => {
    switch (category.toLowerCase()) {
      case "technical sheet":
        return "px-2.5 py-1 text-xs font-semibold text-blue-800 bg-blue-100 rounded-full";
      case "user guide":
        return "px-2.5 py-1 text-xs font-semibold text-green-800 bg-green-100 rounded-full";
      case "api documentation":
        return "px-2.5 py-1 text-xs font-semibold text-purple-800 bg-purple-100 rounded-full";
      case "installation guide":
        return "px-2.5 py-1 text-xs font-semibold text-orange-800 bg-orange-100 rounded-full";
      case "quick start guide":
        return "px-2.5 py-1 text-xs font-semibold text-pink-800 bg-pink-100 rounded-full";
      default:
        return "px-2.5 py-1 text-xs font-semibold text-gray-800 bg-gray-100 rounded-full";
    }
  };

  const getSheetIcon = (category: string) => {
    switch (category.toLowerCase()) {
      case "technical sheet":
        return {
          icon: "fa-cogs",
          bgColor: "bg-blue-100",
          iconColor: "text-blue-600",
        };
      case "user guide":
        return {
          icon: "fa-book",
          bgColor: "bg-green-100",
          iconColor: "text-green-600",
        };
      case "api documentation":
        return {
          icon: "fa-code",
          bgColor: "bg-purple-100",
          iconColor: "text-purple-600",
        };
      case "installation guide":
        return {
          icon: "fa-download",
          bgColor: "bg-orange-100",
          iconColor: "text-orange-600",
        };
      case "quick start guide":
        return {
          icon: "fa-play",
          bgColor: "bg-pink-100",
          iconColor: "text-pink-600",
        };
      default:
        return {
          icon: "fa-file-text",
          bgColor: "bg-gray-100",
          iconColor: "text-gray-600",
        };
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(code, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Pagination logic
  const totalPages = Math.ceil(productSheets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProductSheets = productSheets.slice(startIndex, endIndex);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-800"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">
          {t("productSheets.loadFailed")}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Table Controls */}
      <div className="p-4 border-b border-gray-200 bg-gray-50/50">
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex items-center space-x-4">
            <div className="relative w-full sm:w-72">
              <input
                type="text"
                placeholder={t("productSheets.searchPlaceholder")}
                value={searchQuery}
                onChange={onSearchChange}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-gray-800 transition"
              />
              <i className="fa-solid fa-search text-gray-400 absolute left-4 top-1/2 -translate-y-1/2"></i>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button className="px-4 py-2 flex items-center bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium">
              <i className="fa-solid fa-filter mr-2"></i> {t("common.filter")}
            </button>
            <button className="px-4 py-2 flex items-center bg-white border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors text-sm font-medium">
              <i className="fa-solid fa-file-export mr-2"></i> {t("common.export")}
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-600">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                <input
                  type="checkbox"
                  className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                />
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("productSheets.table.sheet")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("productSheets.table.version")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("productSheets.table.category")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("productSheets.table.date")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider"
              >
                {t("productSheets.table.downloads")}
              </th>
              <th
                scope="col"
                className="p-4 font-semibold uppercase tracking-wider text-right"
              >
                {t("productSheets.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {productSheets.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-12 text-center">
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <i className="fa-solid fa-file-text text-gray-400 text-2xl"></i>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {searchQuery
                        ? t("productSheets.empty.noResultsTitle")
                        : t("productSheets.empty.emptyTitle")}
                    </h3>
                    <p className="text-gray-500 text-sm mb-6">
                      {searchQuery
                        ? t("common.tryAdjustingSearch")
                        : t("productSheets.empty.emptyBody")}
                    </p>
                    {!searchQuery && (
                      <button
                        onClick={() => {
                          // This will trigger the parent component to open the modal
                          const uploadButton = document.querySelector(
                            '[data-testid="upload-sheet-btn"]'
                          ) as HTMLButtonElement;
                          if (uploadButton) uploadButton.click();
                        }}
                        className="px-4 py-2 bg-[#111827] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium flex items-center"
                      >
                        <i className="fa-solid fa-upload mr-2"></i>
                        {t("productSheets.uploadFirstSheet")}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              currentProductSheets.map((sheet: ProductSheet) => {
                const iconConfig = getSheetIcon(sheet.category);
                return (
                  <tr key={sheet.id} className="hover:bg-gray-50/50">
                    <td className="p-4">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-gray-800 focus:ring-gray-800"
                      />
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div
                          className={`w-8 h-8 ${iconConfig.bgColor} rounded-lg flex items-center justify-center mr-3`}
                        >
                          <i
                            className={`fa-solid ${iconConfig.icon} ${iconConfig.iconColor} text-sm`}
                          ></i>
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">
                            {sheet.sheetName}
                          </div>
                          <div className="text-gray-500 text-xs">
                            {sheet.description || t("common.noDescription")}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 whitespace-nowrap font-medium text-gray-900">
                      v{sheet.version}
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      <span className={getCategoryBadgeColor(sheet.category)}>
                        {sheet.category}
                      </span>
                    </td>
                    <td className="p-4 whitespace-nowrap">
                      {formatDate(sheet.createdAt)}
                    </td>
                    <td className="p-4 whitespace-nowrap">{sheet.downloads}</td>
                    <td className="p-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEdit(sheet)}
                          className="text-blue-600 hover:text-blue-800 font-medium text-sm"
                        >
                          <i className="fa-solid fa-edit mr-1"></i>
                          {t("common.edit")}
                        </button>
                        <button
                          onClick={() =>
                            handleDelete(sheet.id, sheet.sheetName)
                          }
                          className="text-red-600 hover:text-red-800 font-medium text-sm"
                        >
                          <i className="fa-solid fa-trash mr-1"></i>
                          {t("common.delete")}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/50">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:justify-between">
            <div className="text-xs sm:text-sm text-gray-700">
              {t("common.showingRange", {
                from: startIndex + 1,
                to: Math.min(endIndex, productSheets.length),
                total: productSheets.length,
                entity: t("common.results"),
              })}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {t("common.previous")}
              </button>
              <span className="text-sm text-gray-700">
                {t("common.pageOf", { current: currentPage, total: totalPages })}
              </span>
              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                {t("common.next")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <ProductSheetModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        editProductSheet={editingProductSheet}
        onSuccess={handleCloseEditModal}
      />
    </div>
  );
};

export default ProductSheetsList;
